import {
  CloseOutlined,
  DownOutlined,
  LeftOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { useDebounce } from '@uidotdev/usehooks'
import {
  Button,
  Checkbox,
  Drawer,
  Input,
  InputNumber,
  Popover,
  Slider,
  Tooltip,
} from 'antd'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { DELAY } from '@/constants'
import useNomenclatureEditorSearch from '@/hooks/search/useNomenclatureEditorSearch'
import useSearch from '@/hooks/search/useSearch'
import useUpdateFilters from '@/hooks/search/useUpdateFilters'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { filtersService } from '@/services/filters/FiltersService'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useNomenclatureEditorFeedStore } from '@/stores/feed/nomenclatureEditorFeedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  CharacteristicFilterType,
  CharacteristicFilterWithRange,
  CharacteristicOptionType,
  ChosenCharacteristicRangeType,
  EntityFilterType,
  StoreShortWithSupplierType,
} from '@/types'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { cleanTechnicalSuffix } from '@/utils/catalog/cleanDescription'
import {
  countSelectedSlots,
  formatSlottedValues,
  formatSlotValue,
  getSlotLabel,
  groupSlotOptions,
  hasUniformEligibleSlottedFilter,
  isSlottedCharacteristicType,
  isUniformSlottedEligible,
  looksLikeSlottedValues,
  SLOTTED_CHARACTERISTIC_TYPE,
} from '@/utils/search/characteristics/slottedCharacteristics'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import type { CharacteristicChipRelevance } from '../Filters.utils'
import {
  formatCharacteristicValue,
  formatRangeLabel,
  getCharacteristicChipRelevance,
  getSelectedCharacteristicValues,
  IRRELEVANT_CHARACTERISTIC_TOOLTIP,
  pinSelectedOptions,
  sortChosenFirst,
} from '../Filters.utils'
import PriorityFiltersRow from '../PriorityFiltersRow/PriorityFiltersRow'
import UniformSlottedCheckbox from '../UniformSlottedCheckbox/UniformSlottedCheckbox'
import FilterChipValues from './FilterChipValues/FilterChipValues'
import cn from './SelectedFilters.module.scss'

const formatValue = (val: string) => formatCharacteristicValue(val)

const NUMERIC_PRIORITY_LIMIT = 4
const NUMERIC_LOAD_STEP = 5
const MAX_PRIORITY_VALUE = 2147483647

type FilterOption = CharacteristicOptionType & { description?: string }

interface ApplyParamsOverrides {
  withLeftovers?: boolean
  withDiscontinued?: boolean
  matchUniformSlottedCharacteristics?: boolean
  parents?: EntityFilterType[]
  categories?: EntityFilterType[]
  brands?: EntityFilterType[]
  stores?: StoreShortWithSupplierType[]
  characteristics?: CharacteristicFilterType[]
}

interface FilterItemProps {
  label: string
  filterCode: number
  filterType: CharacteristicFilterWithRange['type']
  availableOptions?: FilterOption[]
  availableMin?: number | null
  availableMax?: number | null
  selectedValues: string[]
  selectedRange?: ChosenCharacteristicRangeType
  unit?: string
  onApply: (payload: {
    values: string[]
    range: ChosenCharacteristicRangeType | null
    matchUniform?: boolean
  }) => void
  onDelete?: () => void
  themeClass: string
  initialTotal?: number
  slotsCount?: number | null
  relevance?: CharacteristicChipRelevance
}

const FilterItem = ({
  label,
  filterCode,
  filterType,
  availableOptions = [],
  availableMin,
  availableMax,
  selectedValues,
  selectedRange,
  unit = '',
  onApply,
  onDelete,
  themeClass,
  initialTotal,
  slotsCount,
  relevance,
}: FilterItemProps) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [draftOptions, setDraftOptions] = useState<Set<string>>(
    new Set(selectedValues)
  )
  const [draftRange, setDraftRange] =
    useState<ChosenCharacteristicRangeType | null>(
      selectedRange?.min != null || selectedRange?.max != null
        ? {
            min: selectedRange.min ?? null,
            max: selectedRange.max ?? null,
          }
        : null
    )
  const [sliderPreviewRange, setSliderPreviewRange] =
    useState<ChosenCharacteristicRangeType | null>(
      selectedRange?.min != null || selectedRange?.max != null
        ? {
            min: selectedRange.min ?? null,
            max: selectedRange.max ?? null,
          }
        : null
    )
  const [draftMatchUniform, setDraftMatchUniform] = useState(false)
  const [visibleCount, setVisibleCount] = useState(15)

  const [draftTotal, setDraftTotal] = useState<number | null>(
    initialTotal ?? null
  )
  const [isUpdatingCount, setIsUpdatingCount] = useState(false)
  const [localOptions, setLocalOptions] = useState(availableOptions)
  const [localRangeBounds, setLocalRangeBounds] = useState({
    min: availableMin ?? 0,
    max: availableMax ?? 1000,
  })
  const [numericVisibleExtraCount, setNumericVisibleExtraCount] = useState(0)
  // Ответы фасетного пересчёта могут вернуться не в том порядке, в каком ушли
  // запросы. Без этого счётчика более старый ответ затирал бы более свежий,
  // и в фильтре оставались бы значения от предыдущего выбора.
  const facetRequestIdRef = useRef(0)
  const numericOptionsScrollRef = useRef<HTMLDivElement>(null)
  const [numericArrows, setNumericArrows] = useState({
    left: false,
    right: false,
  })

  const {
    searchTerm,
    withLeftovers,
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
    currentlySearchableParents,
    availableFilters,
    matchUniformSlottedCharacteristics,
  } = useSearchStore()

  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )
  const updateFilters = useUpdateFilters()

  const screen = useContext(ScreenSizeContext)
  const isMobile = !!screen?.xs
  const isNumeric = filterType === 'NUMERIC'
  const isSlotted = isSlottedCharacteristicType(filterType)

  const hasAssignedPriority = useCallback((priorityValue: unknown) => {
    const priority = Number(priorityValue)
    return (
      Number.isFinite(priority) && priority > 0 && priority < MAX_PRIORITY_VALUE
    )
  }, [])

  const normalizeRange = useCallback(
    (range: ChosenCharacteristicRangeType | null | undefined) => {
      const min = range?.min ?? null
      const max = range?.max ?? null
      if (min == null && max == null) return null
      return { min, max }
    },
    []
  )

  const debouncedDraftString = useDebounce(
    JSON.stringify({
      values: Array.from(draftOptions).sort(),
      range:
        isNumeric && draftOptions.size > 0 ? null : normalizeRange(draftRange),
      matchUniform: isSlotted ? draftMatchUniform : null,
    }),
    DELAY
  )

  useEffect(() => {
    if (!open) return
    const updateFacetedFilters = async () => {
      const requestId = facetRequestIdRef.current + 1
      facetRequestIdRef.current = requestId

      const availChar = availableFilters?.characteristics?.find(
        (c) => c.code === filterCode
      )
      const normalizedRange =
        isNumeric && draftOptions.size > 0 ? null : normalizeRange(draftRange)
      const draftChar: CharacteristicFilterWithRange = {
        code: filterCode,
        name: availChar?.name ?? '',
        type: availChar?.type ?? filterType,
        unitOfMeasurementDto: availChar?.unitOfMeasurementDto ?? null,
        min: availChar?.min ?? availableMin ?? null,
        max: availChar?.max ?? availableMax ?? null,
        options: Array.from(draftOptions).map((v) => ({ value: v, count: 0 })),
        chosenOptions: [],
        chosenRange: isNumeric ? (normalizedRange ?? undefined) : undefined,
      }
      const shouldIncludeDraftChar = isNumeric
        ? normalizedRange !== null || draftOptions.size > 0
        : draftOptions.size > 0
      const draftCharacteristics: CharacteristicFilterWithRange[] = [
        ...currentlySearchableCharacteristics.filter(
          (c) => c.code !== filterCode
        ),
        ...(shouldIncludeDraftChar ? [draftChar] : []),
      ]
      const draftState = {
        searchWithLeftovers: withLeftovers,
        searchWithDiscontinued: false,
        selectedStores: currentlySearchableStores,
        selectedBrands: currentlySearchableBrands,
        selectedCategories: currentlySearchableCategories,
        selectedParents: currentlySearchableParents,
        selectedCharacteristics: draftCharacteristics,
        searchMatchUniformSlotted: isSlotted
          ? draftMatchUniform
          : matchUniformSlottedCharacteristics,
      }
      const appliedState = {
        withLeftovers,
        withDiscontinued: false,
        stores: currentlySearchableStores,
        brands: currentlySearchableBrands,
        categories: currentlySearchableCategories,
        parents: currentlySearchableParents,
        characteristics: currentlySearchableCharacteristics,
        matchUniformSlottedCharacteristics,
      }
      const draftStateString =
        filtersService.state.createFiltersStateString(draftState)
      const appliedStateString = filtersService.state.createAppliedStateString(
        appliedState.withLeftovers,
        appliedState.withDiscontinued,
        appliedState.stores,
        appliedState.brands,
        appliedState.categories,
        appliedState.parents,
        appliedState.characteristics,
        appliedState.matchUniformSlottedCharacteristics
      )
      const willFetch = draftStateString !== appliedStateString
      if (willFetch) setIsUpdatingCount(true)

      const result = await filtersService.handleFacetedFilterUpdate(
        {
          searchTerm,
          draftState,
          appliedState,
          availableFilters,
          totalElements: initialTotal ?? 0,
        },
        updateFilters
      )
      // Пока ждали ответ, пользователь успел кликнуть ещё раз — этот ответ устарел
      if (facetRequestIdRef.current !== requestId) return

      if (result) {
        setDraftTotal(result.totalElements)
        const responseCharacteristics = result.availableFilters?.characteristics
        const updatedChar = responseCharacteristics?.find(
          (c) => c.code === filterCode
        )

        if (updatedChar) {
          // Пустые поля бэкенд вырезает из JSON, поэтому отсутствие `options`
          // означает «значений не осталось», а не «оставь прежние». Со старой
          // проверкой `if (updatedChar?.options)` в фильтре навсегда зависал бы
          // предыдущий фасет.
          setLocalOptions(updatedChar.options ?? [])
        } else if (responseCharacteristics) {
          console.warn(
            `Фильтр ${filterCode}: характеристика не пришла в ответе ` +
              '/nomenclatures/filters — значения фасета остались прежними.',
            responseCharacteristics.map((c) => c.code)
          )
        }

        if (
          updatedChar &&
          (updatedChar.min != null || updatedChar.max != null)
        ) {
          setLocalRangeBounds({
            min: updatedChar.min ?? availableMin ?? 0,
            max: updatedChar.max ?? availableMax ?? 1000,
          })
        }
      }
      setIsUpdatingCount(false)
    }
    updateFacetedFilters()
  }, [
    debouncedDraftString,
    searchTerm,
    isEditor,
    isNumeric,
    normalizeRange,
    filterType,
    availableMin,
    availableMax,
  ])

  useEffect(() => {
    setVisibleCount(15)
  }, [searchValue])

  useEffect(() => {
    if (!open || isMobile) return

    const closeOnScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Element && target.closest(`.${cn.customPopover}`)) {
        return
      }
      setOpen(false)
    }

    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('wheel', closeOnScroll, { passive: true })
    window.addEventListener('touchmove', closeOnScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('wheel', closeOnScroll)
      window.removeEventListener('touchmove', closeOnScroll)
    }
  }, [open, isMobile])

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setDraftOptions(new Set(selectedValues))
      setDraftRange(
        selectedRange?.min != null || selectedRange?.max != null
          ? {
              min: selectedRange.min ?? null,
              max: selectedRange.max ?? null,
            }
          : null
      )
      setSliderPreviewRange(
        selectedRange?.min != null || selectedRange?.max != null
          ? {
              min: selectedRange.min ?? null,
              max: selectedRange.max ?? null,
            }
          : null
      )
      setSearchValue('')
      setLocalOptions(availableOptions)
      setLocalRangeBounds({
        min: availableMin ?? 0,
        max: availableMax ?? 1000,
      })
      setNumericVisibleExtraCount(0)
      setDraftTotal(initialTotal ?? null)
      setDraftMatchUniform(matchUniformSlottedCharacteristics)
    } else {
      setLocalOptions(availableOptions)
    }
    setOpen(newOpen)
    if (!newOpen) focusProductSearchInput(350)
  }

  const handleLocalReset = () => {
    if (isNumeric) {
      setDraftRange(null)
      setSliderPreviewRange(null)
      setDraftOptions(new Set())
      return
    }
    setDraftOptions(new Set())
  }

  const handleApplyClick = () => {
    onApply({
      values: Array.from(draftOptions),
      range:
        isNumeric && draftOptions.size > 0 ? null : normalizeRange(draftRange),
      matchUniform: isSlotted ? draftMatchUniform : undefined,
    })
    setOpen(false)
    focusProductSearchInput(350)
  }

  const displayList = useMemo(() => {
    let list = pinSelectedOptions(localOptions, selectedValues)
    if (searchValue) {
      list = list.filter((o) =>
        String(o.value || o.description)
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
    }
    return list
  }, [localOptions, selectedValues, searchValue])

  const visibleList = displayList.slice(0, visibleCount)

  const numericDisplayOptions = useMemo(() => {
    if (!isNumeric) {
      return {
        visibleOptions: [] as FilterOption[],
        hasMore: false,
      }
    }

    const uniqueOptions = new Map<string, FilterOption>()

    localOptions.forEach((opt) => {
      const value = String(opt.value || opt.description)
      if (!uniqueOptions.has(value)) {
        uniqueOptions.set(value, opt)
      }
    })

    draftOptions.forEach((value) => {
      if (!uniqueOptions.has(value)) {
        uniqueOptions.set(value, {
          value,
          count: 0,
          priority: Number.MAX_SAFE_INTEGER,
        })
      }
    })

    const pinnedValues = new Set(selectedValues)
    const isPinned = (opt: FilterOption) =>
      pinnedValues.has(String(opt.value || opt.description))
    const orderedOptions = pinSelectedOptions(
      Array.from(uniqueOptions.values()),
      selectedValues
    )
    const pinnedOptions = orderedOptions.filter(isPinned)
    const allOptions = orderedOptions.filter((opt) => !isPinned(opt))
    const assignedPriorityOptions = allOptions
      .filter((opt) => hasAssignedPriority(opt.priority))
      .sort((a, b) => Number(a.priority) - Number(b.priority))

    const firstPriority = assignedPriorityOptions[0]?.priority
    const priorityOptions =
      firstPriority != null
        ? assignedPriorityOptions.filter((opt) => {
            const priority = Number(opt.priority)
            return (
              priority >= Number(firstPriority) &&
              priority < Number(firstPriority) + NUMERIC_PRIORITY_LIMIT
            )
          })
        : allOptions.slice(0, NUMERIC_PRIORITY_LIMIT)

    const visiblePriorityValues = new Set(
      priorityOptions.map((opt) => String(opt.value || opt.description))
    )

    const restOptions = assignedPriorityOptions.filter(
      (opt) => !visiblePriorityValues.has(String(opt.value || opt.description))
    )

    return {
      visibleOptions: [
        ...pinnedOptions,
        ...priorityOptions,
        ...restOptions.slice(0, numericVisibleExtraCount),
      ],
      hasMore: restOptions.length > numericVisibleExtraCount,
    }
  }, [
    isNumeric,
    localOptions,
    draftOptions,
    selectedValues,
    numericVisibleExtraCount,
    hasAssignedPriority,
  ])

  const draftValues = useMemo(() => Array.from(draftOptions), [draftOptions])

  // Бэкенд считает фасет отдельно для каждого присоединения: фильтр самого
  // присоединения из его же корзины исключается (множественный выбор = ИЛИ),
  // а фильтры соседних присоединений применяются (= И). Поэтому значения
  // текущего слота остаются в ответе после выбора, а соседние сужаются —
  // копить ничего не нужно, список берём из свежего ответа как есть.
  const slotGroups = useMemo(
    () =>
      isSlotted
        ? groupSlotOptions(
            localOptions,
            draftValues,
            slotsCount,
            selectedValues
          )
        : [],
    [isSlotted, localOptions, draftValues, slotsCount, selectedValues]
  )

  const draftSlottedSummary = useMemo(
    () => (isSlotted ? formatSlottedValues(Array.from(draftOptions)) : ''),
    [isSlotted, draftOptions]
  )

  const toggleDraftValue = useCallback((value: string) => {
    setDraftOptions((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }, [])

  const isActive =
    selectedRange?.min != null ||
    selectedRange?.max != null ||
    selectedValues.length > 0

  const isMuted = relevance?.isCharacteristicIrrelevant === true
  const irrelevantValues = relevance?.irrelevantValues ?? []
  const labelWithUnit = isNumeric && unit ? `${label} (${unit})` : label

  const isRangeTemporarilyDisabled = isNumeric && draftOptions.size > 0

  const isDraftUniformEligible = useMemo(
    () => isUniformSlottedEligible(Array.from(draftOptions)),
    [draftOptions]
  )

  const isOtherCharacteristicUniformEligible = useMemo(
    () =>
      hasUniformEligibleSlottedFilter(
        currentlySearchableCharacteristics.filter((c) => c.code !== filterCode)
      ),
    [currentlySearchableCharacteristics, filterCode]
  )

  const isDraftFlagApplicable =
    isDraftUniformEligible || isOtherCharacteristicUniformEligible

  useEffect(() => {
    if (isSlotted && draftMatchUniform && !isDraftFlagApplicable) {
      setDraftMatchUniform(false)
    }
  }, [isSlotted, draftMatchUniform, isDraftFlagApplicable])

  const hasChanges = useMemo(() => {
    if (
      isSlotted &&
      (draftMatchUniform && isDraftFlagApplicable) !==
        matchUniformSlottedCharacteristics
    ) {
      return true
    }
    if (isNumeric) {
      const selectedMin =
        selectedValues.length > 0 ? null : (selectedRange?.min ?? null)
      const selectedMax =
        selectedValues.length > 0 ? null : (selectedRange?.max ?? null)
      const draftMin = draftOptions.size > 0 ? null : (draftRange?.min ?? null)
      const draftMax = draftOptions.size > 0 ? null : (draftRange?.max ?? null)
      if (selectedMin !== draftMin || selectedMax !== draftMax) return true
      if (draftOptions.size !== selectedValues.length) return true
      return selectedValues.some((v) => !draftOptions.has(v))
    }
    if (draftOptions.size !== selectedValues.length) return true
    return selectedValues.some((v) => !draftOptions.has(v))
  }, [
    isNumeric,
    isSlotted,
    draftMatchUniform,
    isDraftFlagApplicable,
    matchUniformSlottedCharacteristics,
    draftRange,
    selectedRange,
    draftOptions,
    selectedValues,
  ])

  const isPendingUpdate = isUpdatingCount
  const rangeMin = localRangeBounds.min
  const rangeMax = localRangeBounds.max
  const sliderValue: [number, number] = [
    sliderPreviewRange?.min ?? draftRange?.min ?? rangeMin,
    sliderPreviewRange?.max ?? draftRange?.max ?? rangeMax,
  ]

  const setRange = (
    range: ChosenCharacteristicRangeType | null,
    options?: { syncDraft?: boolean }
  ) => {
    const normalized = normalizeRange(range)
    setSliderPreviewRange(normalized)
    if (options?.syncDraft !== false) {
      setDraftRange(normalized)
    }
  }

  const handleMinInputChange = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      setRange({ min: null, max: draftRange?.max ?? null })
      return
    }

    const currentMax = draftRange?.max ?? rangeMax
    const clamped = Math.max(rangeMin, Math.min(value, currentMax))
    setRange({ min: clamped, max: currentMax })
  }

  const handleMaxInputChange = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      setRange({ min: draftRange?.min ?? null, max: null })
      return
    }

    const currentMin = draftRange?.min ?? rangeMin
    const clamped = Math.min(rangeMax, Math.max(value, currentMin))
    setRange({ min: currentMin, max: clamped })
  }

  const updateNumericArrows = useCallback(() => {
    if (!numericOptionsScrollRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } =
      numericOptionsScrollRef.current
    setNumericArrows({
      left: scrollLeft > 8,
      right: scrollLeft + clientWidth < scrollWidth - 8,
    })
  }, [])

  useEffect(() => {
    if (!open || !isNumeric) return

    const rafId = requestAnimationFrame(updateNumericArrows)
    window.addEventListener('resize', updateNumericArrows)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateNumericArrows)
    }
  }, [open, isNumeric, updateNumericArrows, numericDisplayOptions])

  const handleNumericScroll = (direction: 'left' | 'right') => {
    const offset = direction === 'left' ? -220 : 220
    numericOptionsScrollRef.current?.scrollBy({
      left: offset,
      behavior: 'smooth',
    })
  }

  const filterContent = (
    <div className={cn.filterPopupInner}>
      <div className={cn.popoverHeader}>
        <span className={cn.popoverTitle}>
          {label}
          {unit && <span className={cn.popoverUnit}>({unit})</span>}
        </span>
        <Button
          type="text"
          size="small"
          onClick={handleLocalReset}
          className={cn.resetLink}
        >
          Сбросить <DownOutlined className={cn.resetIcon} />
        </Button>
      </div>

      {isNumeric ? (
        <div className={cn.numericRangeBlock}>
          <div
            className={`${cn.inputsRow} ${isRangeTemporarilyDisabled ? cn.numericRangeDisabled : ''}`}
          >
            <div className={cn.inputWrapper}>
              <label>От{unit ? `, ${unit}` : ''}</label>
              <InputNumber
                controls={false}
                min={rangeMin}
                max={rangeMax}
                value={sliderPreviewRange?.min ?? draftRange?.min ?? undefined}
                placeholder={String(rangeMin)}
                step={0.01}
                precision={1}
                disabled={isRangeTemporarilyDisabled}
                onChange={handleMinInputChange}
              />
            </div>
            <div className={cn.inputWrapper}>
              <label>До{unit ? `, ${unit}` : ''}</label>
              <InputNumber
                controls={false}
                min={rangeMin}
                max={rangeMax}
                value={sliderPreviewRange?.max ?? draftRange?.max ?? undefined}
                placeholder={String(rangeMax)}
                step={0.01}
                precision={1}
                disabled={isRangeTemporarilyDisabled}
                onChange={handleMaxInputChange}
              />
            </div>
          </div>

          {rangeMin !== rangeMax && (
            <div
              className={`${cn.sliderWrapper} ${themeClass} ${isRangeTemporarilyDisabled ? cn.numericRangeDisabled : ''}`}
            >
              <Slider
                range
                min={rangeMin}
                max={rangeMax}
                value={sliderValue}
                disabled={isRangeTemporarilyDisabled}
                onChange={(values) => {
                  const [nextMin, nextMax] = values as [number, number]
                  setRange({ min: nextMin, max: nextMax }, { syncDraft: false })
                }}
                onChangeComplete={(values) => {
                  const [nextMin, nextMax] = values as [number, number]
                  setDraftRange(normalizeRange({ min: nextMin, max: nextMax }))
                }}
                tooltip={{ open: false }}
              />
            </div>
          )}

          {numericDisplayOptions.visibleOptions.length > 0 && (
            <div className={cn.numericOptionsSection}>
              <div className={cn.numericOptionsScrollerWrap}>
                {numericArrows.left && (
                  <button
                    type="button"
                    className={`${cn.numericScrollArrow} ${cn.left}`}
                    onClick={() => handleNumericScroll('left')}
                  >
                    <LeftOutlined />
                  </button>
                )}

                <div
                  ref={numericOptionsScrollRef}
                  className={cn.numericOptionsScroller}
                  onScroll={updateNumericArrows}
                >
                  {numericDisplayOptions.visibleOptions.map((opt, index) => {
                    const val = String(opt.value || opt.description)
                    const isChecked = draftOptions.has(val)

                    return (
                      <label
                        key={`${val}-${index}`}
                        className={`${cn.numericOptionCard} ${isChecked ? cn.numericOptionCardChecked : ''}`}
                      >
                        <Checkbox
                          className={`${cn.selectedFiltersCheckbox} ${themeClass === cn.themeBlue ? cn.checkboxBlue : ''}`}
                          checked={isChecked}
                          onChange={(e) => {
                            const nextSet = new Set(draftOptions)
                            if (e.target.checked) nextSet.add(val)
                            else nextSet.delete(val)
                            setDraftOptions(nextSet)
                          }}
                        />
                        <span className={cn.numericOptionValue}>
                          {formatValue(val)}
                        </span>
                        {opt.count !== undefined && (
                          <span className={cn.numericOptionCount}>
                            {opt.count}
                          </span>
                        )}
                      </label>
                    )
                  })}

                  {numericDisplayOptions.hasMore && (
                    <button
                      type="button"
                      className={cn.numericMoreButton}
                      onClick={() => {
                        setNumericVisibleExtraCount(
                          (prev) => prev + NUMERIC_LOAD_STEP
                        )
                      }}
                    >
                      Еще
                    </button>
                  )}
                </div>

                {numericArrows.right && (
                  <button
                    type="button"
                    className={`${cn.numericScrollArrow} ${cn.right}`}
                    onClick={() => handleNumericScroll('right')}
                  >
                    <RightOutlined />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : isSlotted ? (
        <>
          {/* Закреплено над прокручиваемым списком — видно на уровне заголовка */}
          <div className={cn.slotSummaryBar}>
            <span className={cn.slotHint}>
              {draftSlottedSummary ? (
                <>
                  Итоговое значение: <strong>{draftSlottedSummary}</strong>
                </>
              ) : (
                'Выберите диаметры для каждого присоединения'
              )}
            </span>
            <UniformSlottedCheckbox
              checked={draftMatchUniform && isDraftUniformEligible}
              disabled={!isDraftUniformEligible}
              onChange={setDraftMatchUniform}
            />
          </div>
          <div className={cn.slottedBlock}>
            {slotGroups.length === 0 ? (
              <div className={cn.emptyText}>Ничего не найдено</div>
            ) : (
              <>
                {slotGroups.map((group) => (
                  <div className={cn.slotGroup} key={group.index}>
                    <div className={cn.slotTitle}>
                      {getSlotLabel(group.index)}
                    </div>
                    {group.options.length === 0 && (
                      <span className={cn.slotEmpty}>
                        Нет доступных значений
                      </span>
                    )}
                    <div className={cn.slotPills}>
                      {group.options.map((option) => {
                        const isChecked = draftOptions.has(option.rawValue)

                        return (
                          <button
                            type="button"
                            key={option.rawValue}
                            className={`${cn.slotPill} ${isChecked ? cn.slotPillChecked : ''}`}
                            aria-pressed={isChecked}
                            title={`${formatSlotValue(option.value)}${unit ? ` ${unit}` : ''}`}
                            onClick={() => toggleDraftValue(option.rawValue)}
                          >
                            {formatSlotValue(option.value)}
                            <span className={cn.slotPillCount}>
                              {option.count}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <Input
            placeholder="Поиск..."
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className={cn.popoverSearch}
          />
          <div className={cn.popoverList}>
            {visibleList.length > 0 ? (
              <>
                {visibleList.map((opt, index) => {
                  const val = String(opt.value || opt.description)
                  const isChecked = draftOptions.has(val)
                  return (
                    <div
                      key={`${val}-${index}`}
                      className={cn.popoverListItem}
                      onClick={() => {
                        const newSet = new Set(draftOptions)
                        if (isChecked) {
                          newSet.delete(val)
                        } else {
                          newSet.add(val)
                        }
                        setDraftOptions(newSet)
                      }}
                    >
                      <Checkbox
                        className={`${cn.selectedFiltersCheckbox} ${themeClass === cn.themeBlue ? cn.checkboxBlue : ''}`}
                        checked={isChecked}
                      />
                      <span className={cn.optLabel}>{formatValue(val)}</span>
                      {opt.count !== undefined && (
                        <span className={cn.optCount}>{opt.count}</span>
                      )}
                    </div>
                  )
                })}
                {visibleCount < displayList.length && (
                  <div className={cn.showMoreRow}>
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation()
                        setVisibleCount((prev) => prev + 15)
                      }}
                    >
                      Показать еще...
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className={cn.emptyText}>Ничего не найдено</div>
            )}
          </div>
        </>
      )}

      <div className={cn.popoverFooter}>
        <Button
          onClick={handleApplyClick}
          type="primary"
          loading={isPendingUpdate}
          disabled={!hasChanges && !isPendingUpdate}
          className={
            themeClass === cn.themeBlue ? cn.applyBtnBlue : cn.applyBtnOrange
          }
        >
          {isPendingUpdate
            ? 'Обновление...'
            : `Показать (${draftTotal ?? '...'})`}
        </Button>
      </div>
    </div>
  )

  const renderTag = () => {
    if (isSlotted) {
      const selectedSlotsCount = countSelectedSlots(selectedValues)

      return (
        <div className={cn.tagMainArea}>
          <span className={cn.truncateText}>
            {label}:
            {isActive && (
              <span className={cn.valuesInline}>
                {isMobile && selectedSlotsCount > 2 ? (
                  <span className={cn.countBadge}>{selectedSlotsCount}</span>
                ) : (
                  <FilterChipValues
                    values={selectedValues}
                    irrelevantValues={irrelevantValues}
                    isSlotted
                    formatValue={formatValue}
                  />
                )}
              </span>
            )}
          </span>
          {isActive && isMuted && (
            <Tooltip title={IRRELEVANT_CHARACTERISTIC_TOOLTIP}>
              <QuestionCircleOutlined className={cn.irrelevantIcon} />
            </Tooltip>
          )}
          <DownOutlined className={cn.arrowIcon} />
        </div>
      )
    }

    return (
      <div className={cn.tagMainArea}>
        <span className={cn.truncateText}>
          {labelWithUnit}:
          {isActive && (
            <span className={cn.valuesInline}>
              {selectedRange?.min != null ||
              selectedRange?.max != null ||
              (isNumeric && selectedValues.length > 0) ? (
                <>
                  {(selectedRange?.min != null ||
                    selectedRange?.max != null) && (
                    <span>{` ${formatRangeLabel(selectedRange ?? {})}`}</span>
                  )}
                  {isNumeric &&
                    selectedValues.length > 0 &&
                    (isMobile ? (
                      <span className={cn.countBadge}>
                        {selectedValues.length}
                      </span>
                    ) : selectedValues.length > 2 ? (
                      <span className={cn.countBadge}>
                        {selectedValues.length}
                      </span>
                    ) : (
                      <FilterChipValues
                        values={selectedValues}
                        irrelevantValues={irrelevantValues}
                        isSlotted={false}
                        formatValue={formatValue}
                      />
                    ))}
                </>
              ) : isMobile ? (
                selectedValues.length > 0 && (
                  <span className={cn.countBadge}>{selectedValues.length}</span>
                )
              ) : selectedValues.length > 2 ? (
                <span className={cn.countBadge}>{selectedValues.length}</span>
              ) : (
                <FilterChipValues
                  values={selectedValues}
                  irrelevantValues={irrelevantValues}
                  isSlotted={false}
                  formatValue={formatValue}
                />
              )}
            </span>
          )}
        </span>
        {isActive && isMuted && (
          <Tooltip title={IRRELEVANT_CHARACTERISTIC_TOOLTIP}>
            <QuestionCircleOutlined className={cn.irrelevantIcon} />
          </Tooltip>
        )}
        <DownOutlined className={cn.arrowIcon} />
      </div>
    )
  }

  const tagClassName = [
    cn.filterTag,
    isActive ? `${cn.tagApplied} ${themeClass}` : cn.tagDefault,
    isActive && isMuted ? cn.tagIrrelevant : '',
    open ? cn.popoverOpen : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={tagClassName}>
      {isMobile ? (
        <>
          <div onClick={() => handleOpenChange(true)} className={cn.tagTrigger}>
            {renderTag()}
          </div>
          <Drawer
            placement="bottom"
            onClose={() => {
              setOpen(false)
              focusProductSearchInput(350)
            }}
            open={open}
            height="auto"
            closable={true}
            className={cn.mobileFilterDrawer}
            styles={{ body: { padding: 0 } }}
          >
            {filterContent}
          </Drawer>
        </>
      ) : (
        <Popover
          content={<div className={cn.popoverContent}>{filterContent}</div>}
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomLeft"
          align={{
            overflow: { adjustX: false, adjustY: false },
          }}
          rootClassName={`${cn.customPopover} ${themeClass}`}
        >
          <div className={`${cn.tagTrigger} ${cn.tagTriggerClickable}`}>
            {renderTag()}
          </div>
        </Popover>
      )}

      {isActive && onDelete && (
        <div
          className={cn.deleteIconWrapper}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
            setOpen(false)
            focusProductSearchInput(350)
          }}
        >
          <CloseOutlined />
        </div>
      )}
    </div>
  )
}

const SimpleTag = ({
  label,
  value,
  onDelete,
  themeClass,
}: {
  label: string
  value: string
  onDelete?: () => void
  themeClass: string
}) => {
  const tagClassName = `${cn.filterTag} ${cn.tagApplied} ${themeClass}`

  return (
    <div className={tagClassName}>
      <div className={`${cn.tagMainArea} ${cn.tagMainAreaStatic}`}>
        <span className={cn.truncateText}>
          {label}: <span className={cn.valuesInline}>{formatValue(value)}</span>
        </span>
      </div>
      {onDelete && (
        <div
          className={cn.deleteIconWrapper}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <CloseOutlined />
        </div>
      )}
    </div>
  )
}

interface SelectedFiltersProps {
  popularTarget?: HTMLElement | null
}

export default function SelectedFilters({
  popularTarget = null,
}: SelectedFiltersProps = {}) {
  const searchStore = useSearchStore()
  const { selectedOrganisation } = useUserInfoStore()
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )
  const themeClass =
    Object.keys(selectedOrganisation).length > 0 ? cn.themeBlue : cn.themeOrange

  const search = useSearch()
  const nomenclatureEditorSearch = useNomenclatureEditorSearch()

  const setProductsLoading = useFeedStore((s) => s.setProductsLoading)
  const feedTotalElements = useFeedStore((s) => s.totalElements)
  const editorTotal = useNomenclatureEditorFeedStore(
    (s) => (s.pagination.total as number) ?? 0
  )
  const setEditorProductsLoading = useNomenclatureEditorFeedStore(
    (s) => s.setProductsLoading
  )
  const totalElements = isEditor ? editorTotal : feedTotalElements

  const applyParams = useCallback(
    (overrides: ApplyParamsOverrides) => {
      filtersService.applyFilters({
        isNomenclatureEditor: isEditor,
        searchTerm: searchStore.searchTerm,
        searchWithLeftovers:
          overrides.withLeftovers ?? searchStore.withLeftovers,
        searchWithDiscontinued:
          overrides.withDiscontinued ?? searchStore.withDiscontinued,
        searchMatchUniformSlottedCharacteristics:
          overrides.matchUniformSlottedCharacteristics ??
          searchStore.matchUniformSlottedCharacteristics,
        selectedParents:
          overrides.parents ?? searchStore.currentlySearchableParents,
        selectedCategories:
          overrides.categories ?? searchStore.currentlySearchableCategories,
        selectedBrands:
          overrides.brands ?? searchStore.currentlySearchableBrands,
        selectedStores:
          overrides.stores ?? searchStore.currentlySearchableStores,
        selectedCharacteristics:
          overrides.characteristics ??
          searchStore.currentlySearchableCharacteristics,
        currentlySearchableNomenclatureGroupId:
          searchStore.currentlySearchableNomenclatureGroupId,
        setters: {
          setProductsLoading,
          setNomenclatureEditorProductsLoading: setEditorProductsLoading,
          setCurrentlySearchableBrands:
            searchStore.setCurrentlySearchableBrands,
          setCurrentlySearchableCategories:
            searchStore.setCurrentlySearchableCategories,
          setCurrentlySearchableStores:
            searchStore.setCurrentlySearchableStores,
          setCurrentlySearchableCharacteristics:
            searchStore.setCurrentlySearchableCharacteristics,
          setCurrentlySearchableParents:
            searchStore.setCurrentlySearchableParents,
          setWithLeftovers: searchStore.setWithLeftovers,
          setWithDiscontinued: searchStore.setWithDiscontinued,
          setMatchUniformSlottedCharacteristics:
            searchStore.setMatchUniformSlottedCharacteristics,
        },
        searchHook: search,
        nomenclatureEditorSearchHook: nomenclatureEditorSearch,
      })
    },
    [isEditor, searchStore, search, nomenclatureEditorSearch]
  )

  const popularCharacteristics = useMemo(() => {
    return (
      searchStore.availableFilters?.characteristics?.filter(
        (c) => c.isMain === true
      ) || []
    )
  }, [searchStore.availableFilters])

  const popularCodes = new Set(popularCharacteristics.map((c) => c.code))

  const appliedOtherCharacteristics =
    searchStore.currentlySearchableCharacteristics.filter(
      (c) => !popularCodes.has(c.code)
    )

  const availableCharacteristicsByCode = new Map(
    (searchStore.availableFilters?.characteristics ?? []).map((c) => [
      c.code,
      c,
    ])
  )

  const renderCharacteristicFilter = (avail: CharacteristicFilterType) => {
    const applied = searchStore.currentlySearchableCharacteristics.find(
      (c) => c.code === avail.code
    ) as CharacteristicFilterWithRange | undefined

    const filterType =
      !availableCharacteristicsByCode.has(avail.code) &&
      applied &&
      looksLikeSlottedValues(getSelectedCharacteristicValues(applied))
        ? SLOTTED_CHARACTERISTIC_TYPE
        : avail.type

    return (
      <FilterItem
        key={avail.code}
        label={cleanTechnicalSuffix(avail.name)}
        filterCode={avail.code}
        filterType={filterType}
        themeClass={themeClass}
        availableOptions={avail.options}
        availableMin={avail.min}
        availableMax={avail.max}
        unit={avail.unitOfMeasurementDto?.description}
        slotsCount={avail.slotsCount}
        selectedValues={
          applied
            ? [
                ...(applied.options?.map((o) => String(o.value)) || []),
                ...(applied.chosenOptions || []),
              ]
            : []
        }
        selectedRange={applied?.chosenRange}
        initialTotal={totalElements}
        relevance={
          applied ? getCharacteristicChipRelevance(applied) : undefined
        }
        onDelete={() =>
          applyParams({
            characteristics:
              searchStore.currentlySearchableCharacteristics.filter(
                (c) => c.code !== avail.code
              ),
          })
        }
        onApply={({ values, range, matchUniform }) => {
          const filtered =
            searchStore.currentlySearchableCharacteristics.filter(
              (c) => c.code !== avail.code
            )
          const hasRange = range?.min != null || range?.max != null
          const hasValues = values.length > 0

          if (filterType === 'NUMERIC' && (hasRange || hasValues)) {
            filtered.push({
              ...avail,
              options: values.map((v) => ({ value: v, count: 0 })),
              chosenOptions: [],
              chosenRange: {
                min: range?.min ?? null,
                max: range?.max ?? null,
              },
            } as CharacteristicFilterWithRange)
          } else if (filterType !== 'NUMERIC' && hasValues) {
            filtered.push({
              ...avail,
              options: values.map((v) => ({ value: v, count: 0 })),
              chosenOptions: [],
              chosenRange: undefined,
            } as CharacteristicFilterWithRange)
          }
          applyParams({
            characteristics: filtered,
            matchUniformSlottedCharacteristics: matchUniform,
          })
        }}
      />
    )
  }

  const hasAppliedFilters =
    searchStore.currentlySearchableParents.length > 0 ||
    appliedOtherCharacteristics.length > 0

  const scrollRefPopular = useRef<HTMLDivElement>(null)
  const scrollRefActive = useRef<HTMLDivElement>(null)

  const [arrowsPopular, setArrowsPopular] = useState({
    left: false,
    right: false,
  })
  const [arrowsActive, setArrowsActive] = useState({
    left: false,
    right: false,
  })

  const checkScroll = (
    ref: React.RefObject<HTMLDivElement>,
    setter: (val: { left: boolean; right: boolean }) => void
  ) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current
      setter({
        left: scrollLeft > 10,
        right: scrollLeft + clientWidth < scrollWidth - 10,
      })
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScroll(scrollRefPopular, setArrowsPopular)
      checkScroll(scrollRefActive, setArrowsActive)
    }, 200)

    window.addEventListener('resize', () => {
      checkScroll(scrollRefPopular, setArrowsPopular)
      checkScroll(scrollRefActive, setArrowsActive)
    })

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', () => {})
    }
  }, [
    popularCharacteristics,
    hasAppliedFilters,
    searchStore.currentlySearchableCharacteristics,
  ])

  const handleArrowClick = (
    ref: React.RefObject<HTMLDivElement>,
    direction: 'left' | 'right'
  ) => {
    const offset = direction === 'left' ? -180 : 180
    ref.current?.scrollBy({ left: offset, behavior: 'smooth' })
  }

  const parentTags = searchStore.currentlySearchableParents.map((p) => (
    <SimpleTag
      key={p.id}
      label="Номенклатурная группа"
      value={p.description}
      themeClass={themeClass}
      onDelete={() =>
        applyParams({
          parents: searchStore.currentlySearchableParents.filter(
            (i) => i.id !== p.id
          ),
        })
      }
    />
  ))
  const isChosen = (code: CharacteristicFilterType['code']) =>
    searchStore.currentlySearchableCharacteristics.some((c) => c.code === code)
  const popularChips = sortChosenFirst(popularCharacteristics, (c) =>
    isChosen(c.code)
  ).map((avail) => renderCharacteristicFilter(avail))
  const appliedOtherChips = appliedOtherCharacteristics.map((char) =>
    renderCharacteristicFilter(
      availableCharacteristicsByCode.get(char.code) ?? char
    )
  )
  const panelCharacteristics = [
    ...popularCharacteristics,
    ...appliedOtherCharacteristics.map(
      (char) => availableCharacteristicsByCode.get(char.code) ?? char
    ),
  ]
  const panelChips = sortChosenFirst(panelCharacteristics, (c) =>
    isChosen(c.code)
  ).map((avail) => renderCharacteristicFilter(avail))
  const panelItemsCount = parentTags.length + panelChips.length

  return (
    <div className={cn.selectedFiltersContainer} data-selected-filters>
      {/* РАЗДЕЛ 1: ПОПУЛЯРНЫЕ ХАРАКТЕРИСТИКИ */}
      {popularTarget &&
        panelItemsCount > 0 &&
        createPortal(
          <PriorityFiltersRow itemsCount={panelItemsCount}>
            {parentTags}
            {panelChips}
          </PriorityFiltersRow>,
          popularTarget
        )}
      {popularTarget && popularCharacteristics.length > 0 && (
        <div className={cn.sectionBlock}>
          <PriorityFiltersRow itemsCount={popularCharacteristics.length}>
            {popularChips}
          </PriorityFiltersRow>
        </div>
      )}
      {!popularTarget && popularCharacteristics.length > 0 && (
        <div className={cn.sectionBlock}>
          <div className={cn.scrollWrapper}>
            {/* Левая стрелка для популярных */}
            {arrowsPopular.left && (
              <div
                className={`${cn.scrollArrow} ${cn.left}`}
                onClick={() => handleArrowClick(scrollRefPopular, 'left')}
              >
                <LeftOutlined />
              </div>
            )}

            <div
              className={cn.filterList}
              ref={scrollRefPopular}
              onScroll={() => checkScroll(scrollRefPopular, setArrowsPopular)}
            >
              {popularChips}
            </div>

            {/* Правая стрелка для популярных */}
            {arrowsPopular.right && (
              <div
                className={`${cn.scrollArrow} ${cn.right}`}
                onClick={() => handleArrowClick(scrollRefPopular, 'right')}
              >
                <RightOutlined />
              </div>
            )}
          </div>
        </div>
      )}

      {/* РАЗДЕЛ 2: АКТИВНЫЕ ФИЛЬТРЫ */}
      {popularTarget && hasAppliedFilters && (
        <div className={cn.sectionBlock}>
          <PriorityFiltersRow
            itemsCount={parentTags.length + appliedOtherChips.length}
          >
            {parentTags}
            {appliedOtherChips}
          </PriorityFiltersRow>
        </div>
      )}
      {!popularTarget && hasAppliedFilters && (
        <div className={cn.sectionBlock}>
          <div className={cn.scrollWrapper}>
            {/* Левая стрелка для активных */}
            {arrowsActive.left && (
              <div
                className={`${cn.scrollArrow} ${cn.left}`}
                onClick={() => handleArrowClick(scrollRefActive, 'left')}
              >
                <LeftOutlined />
              </div>
            )}

            <div
              className={cn.filterList}
              ref={scrollRefActive}
              onScroll={() => checkScroll(scrollRefActive, setArrowsActive)}
            >
              {searchStore.currentlySearchableParents.map((p) => (
                <SimpleTag
                  key={p.id}
                  label="Номенклатурная группа"
                  value={p.description}
                  themeClass={themeClass}
                  onDelete={() =>
                    applyParams({
                      parents: searchStore.currentlySearchableParents.filter(
                        (i) => i.id !== p.id
                      ),
                    })
                  }
                />
              ))}

              {appliedOtherCharacteristics.map((char) =>
                renderCharacteristicFilter(
                  availableCharacteristicsByCode.get(char.code) ?? char
                )
              )}
            </div>

            {/* Правая стрелка для активных */}
            {arrowsActive.right && (
              <div
                className={`${cn.scrollArrow} ${cn.right}`}
                onClick={() => handleArrowClick(scrollRefActive, 'right')}
              >
                <RightOutlined />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
